import { useEffect } from 'react';

/**
 * Stop the page behind an overlay from scrolling while it is open.
 *
 * The obvious version of this — `document.body.style.overflow = 'hidden'` —
 * does nothing here, and the reason is worth writing down: the shell is a
 * full-height flex layout, so the body never scrolls. What scrolls is the
 * `<main>` inside it, and its scrollbar is drawn in the window's gutter,
 * *outside* the viewport an overlay covers.
 *
 * So a file preview left a scrollbar sitting beside it that could be grabbed
 * and dragged and moved nothing anybody could see — it was scrolling the Drive
 * underneath. That is the "stuck scrollbar": not stuck, attached to the wrong
 * thing.
 *
 * Counted rather than set and cleared, because overlays open on top of each
 * other — a preview over a dialog — and the inner one closing must not unlock
 * the page under the outer one.
 */

let open = 0;

export function useScrollLock(active = true): void {
  useEffect(() => {
    if (!active) return undefined;

    open += 1;
    document.body.dataset['modalOpen'] = 'true';

    /*
     * The root as well as the body.
     *
     * Which of the two owns the viewport's scrollbar depends on the page: the
     * body's overflow only stands in for the viewport's while the root's own
     * is `visible`, and this app has pages of both shapes. Locking both is
     * one line and settles it either way.
     *
     * What this does *not* do is stop `scrollTop = 300` — overflow governs
     * the user's wheel, trackpad and scrollbar, not the program's. That is
     * the right behaviour and worth knowing before testing it: the honest
     * measurement is whether a scrollbar exists at all, and under this lock
     * the window's gutter goes from 15 pixels to none.
     */
    const root = document.documentElement;
    const previous = { root: root.style.overflow, body: document.body.style.overflow };
    root.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      open -= 1;
      if (open > 0) return;
      delete document.body.dataset['modalOpen'];
      root.style.overflow = previous.root;
      document.body.style.overflow = previous.body;
    };
  }, [active]);
}
